// src/encounter/services/use-article-search.ts
// Patient-education article search (A&P Education tab). Flutter: FetchArticlesUseCase →
// common_data_source_impl.dart:902 `fetchArticles` → GET {Master}/GetArticles?searchQuery=<q>.
// Returns the raw result GROUPS (ArticlesModel[]); the caller flattens/renders + debounces the input
// (Flutter debounces the SimpleSearchWidget at 600ms — do that at the component, not here).
import { useQuery } from '@tanstack/react-query';
import { get } from '@/shared/api/api-client';
import { articlesModelFromWire, type ArticlesModel, type ArticlesModelWire } from '@/types/education-api';

/**
 * Search patient-education articles by free text. `enabled` gates the fetch (pass false for a blank
 * query so we don't hit the API with an empty search — Flutter clears the list on blank instead).
 */
export function useArticleSearch(query: string, enabled: boolean) {
  return useQuery<ArticlesModel[]>({
    queryKey: ['article-search', query],
    enabled: enabled && query.trim().length > 0,
    queryFn: async ({ signal }) => {
      // The wire is PascalCase (Flutter education_model.dart) — map it to the app shape here.
      const wire = await get<ArticlesModelWire[]>(
        `/api/Master/GetArticles?searchQuery=${encodeURIComponent(query)}`,
        { signal },
      );
      return (wire ?? []).map(articlesModelFromWire);
    },
  });
}
